import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { 
  Globe, Instagram, Phone, Mail, User, X, 
  ChevronLeft, ChevronRight, Sparkles, ShoppingBag, Info, Image as ImageIcon,
  ShieldCheck, Tag, Search, MapPin, 
  Flag, MessageSquare, CheckCircle2, Plus, Minus, Trash2, ShoppingCart, Star, Compass
} from "lucide-react";
import "./VendorPublicProfile.css";
import VendorShareButton from "./VendorShareButton";

const EMPTY_VALUES = ["na", "n/a", "none", "-", ""];
const isEmptyLink = (raw) => !raw || EMPTY_VALUES.includes(raw.trim().toLowerCase());

const toWebsiteUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  return /^https?:\/\//i.test(val) ? val : `https://${val.replace(/^\/+/, "")}`;
};

const toInstagramUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  return /^https?:\/\//i.test(val) ? val : `https://instagram.com/${val.replace(/^@/, "")}`;
};

const VendorPublicProfile = () => {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  
  // Navigation & Search
  const [activeTab, setActiveTab] = useState("shop");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Overlays
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productActiveImg, setProductActiveImg] = useState(0);

  // Vendor-Scoped Cart State
  const [cart, setCart] = useState([]);
  const [showCartDrawer, setShowCartDrawer] = useState(false);

  // Review & Report Overlays
  const [showReviewsDrawer, setShowReviewsDrawer] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // Forms
  const [reviewStars, setReviewStars] = useState(5);
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);

  const [reportReason, setReportReason] = useState("fraud");
  const [reporterEmail, setReporterEmail] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  // Fallback activeTab if window resizes to desktop while "about" tab is open
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && activeTab === "about") {
        setActiveTab("shop");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;

    const loadVendorData = async () => {
      setLoading(true);
      setNotFound(false);

      const { data: profile, error: profileErr } = await supabase
        .from("public_vendor_profiles")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (cancelled) return;

      if (profileErr || !profile) {
        setNotFound(true);
        setVendor(null);
        setLoading(false);
        return;
      }

      setVendor(profile);

      // Load vendor ratings
      const { data: ratingData } = await supabase
        .from("vendor_ratings")
        .select("*")
        .eq("vendor_id", profile.id)
        .order("created_at", { ascending: false });

      if (!cancelled) setRatings(ratingData || []);

      // Load published products
      if (profile.can_list_products) {
        const { data: prods } = await supabase
          .from("products")
          .select("*")
          .eq("vendor_id", profile.id)
          .eq("status", "published")
          .order("created_at", { ascending: false });

        if (!cancelled) setProducts(prods || []);
      }

      if (!cancelled) setLoading(false);
    };

    loadVendorData();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    setCart([]);
  }, [slug]);

  // Cart Operations
  const addToCart = (product, qtyToAdd = 1) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === product.id);
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += qtyToAdd;
        return updated;
      } else {
        return [...prev, { ...product, quantity: qtyToAdd }];
      }
    });
  };

  const updateCartQty = (productId, delta) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id === productId) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const formatMultiItemWhatsAppUrl = () => {
    if (!vendor?.phone_number || cart.length === 0) return null;
    const rawPhone = vendor.phone_number.replace(/[^0-9]/g, "");

    let itemsList = cart
      .map((item) => `• ${item.quantity}x ${item.title} (KES ${(Number(item.price) * item.quantity).toLocaleString()})`)
      .join("\n");

    const msg = encodeURIComponent(
      `Hi ${vendor.brand_name}! I am browsing your BizBaz storefront (bizbazevents.com/v/${vendor.slug}) and would like to order:\n\n${itemsList}\n\n*Total: KES ${cartTotal.toLocaleString()}*\n\nPlease confirm availability and payment details!`
    );

    return `https://wa.me/${rawPhone}?text=${msg}`;
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewerName.trim() || !reviewerEmail.trim()) return;

    setReviewSubmitting(true);
    const { data, error } = await supabase.from("vendor_ratings").insert([{
      vendor_id: vendor.id,
      reviewer_name: reviewerName.trim(),
      reviewer_email: reviewerEmail.trim(),
      rating: reviewStars,
      comment: reviewComment.trim(),
    }]).select().single();

    setReviewSubmitting(false);

    if (!error) {
      setRatings((prev) => [data, ...prev]);
      setReviewSuccess(true);
      setTimeout(() => {
        setReviewSuccess(false);
        setShowReviewModal(false);
        setReviewComment("");
      }, 1500);
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reporterEmail.trim()) return;

    setReportSubmitting(true);
    const { error } = await supabase.from("vendor_reports").insert([{
      vendor_id: vendor.id,
      reporter_email: reporterEmail.trim(),
      reason: reportReason,
      details: reportDetails.trim(),
    }]);

    setReportSubmitting(false);

    if (!error) {
      setReportSuccess(true);
      setTimeout(() => {
        setReportSuccess(false);
        setShowReportModal(false);
        setReportDetails("");
      }, 1500);
    }
  };

  const gallery = vendor?.gallery_paths || [];
  const isLightboxOpen = lightboxIndex !== null;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length)),
    [gallery.length]
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % gallery.length)),
    [gallery.length]
  );

  useEffect(() => {
    if (!isLightboxOpen && !selectedProduct && !showReviewsDrawer && !showCartDrawer) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeLightbox();
        setSelectedProduct(null);
        setShowReviewsDrawer(false);
        setShowCartDrawer(false);
      }
      if (isLightboxOpen && e.key === "ArrowLeft") showPrev();
      if (isLightboxOpen && e.key === "ArrowRight") showNext();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isLightboxOpen, selectedProduct, showReviewsDrawer, showCartDrawer, closeLightbox, showPrev, showNext]);

  if (loading) {
    return (
      <div className="bzv-loading-wrapper">
        <div className="bzv-spinner" />
      </div>
    );
  }

  if (notFound || !vendor) {
    return (
      <div className="bzv-not-found-card">
        <h2>Vendor Not Found</h2>
        <p>This profile doesn't exist or hasn't been published on BizBaz Events yet.</p>
        <Link to="/Directory" className="bzv-primary-action-btn">Discover Other Vendors</Link>
      </div>
    );
  }

  const canListProducts = Boolean(vendor.can_list_products);

  const avgRating = ratings.length > 0 
    ? (ratings.reduce((acc, curr) => acc + curr.rating, 0) / ratings.length).toFixed(1)
    : "5.0";

  const filteredProducts = products.filter((item) => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCat = selectedCategory === "All" || item.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const productCategories = ["All", ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))];

  return (
    <div className="bzv-storefront-wrapper">
      {/* ── Top Bar Container for Desktop Navigation ── */}
      <div className="bzv-top-discover-bar">
        <Link to="/Directory" className="bzv-discover-btn">
          <Compass size={16} />
          <span>Discover More Vendors</span>
        </Link>
      </div>

      {/* ── Cover Hero ── */}
      <div
        className="bzv-cover-hero"
        style={vendor.cover_url ? { backgroundImage: `url(${vendor.cover_url})` } : undefined}
      >
        {!vendor.cover_url && <div className="bzv-cover-fallback" />}
        <div className="bzv-cover-shade" />
      </div>

      {/* ── Main Brand Header ── */}
      <div className="bzv-brand-section">
        <div className="bzv-brand-card">
          <div className="bzv-avatar-box">
            {vendor.logo_url ? (
              <img src={vendor.logo_url} alt={`${vendor.brand_name} logo`} />
            ) : (
              <span className="bzv-avatar-text">{vendor.brand_name?.[0]?.toUpperCase() || "?"}</span>
            )}
          </div>

          <div className="bzv-brand-details">
            <div className="bzv-title-row">
              <h1 className="bzv-brand-heading">{vendor.brand_name}</h1>
              <span className="bzv-badge-verified">
                <ShieldCheck size={13} /> Verified Store
              </span>
            </div>

            <div className="bzv-stats-row">
              <button 
                type="button" 
                className="bzv-stat-trigger" 
                onClick={() => setActiveTab("reviews")}
              >
                <Star size={13} className="bzv-star-filled" /> {avgRating} ({ratings.length} reviews)
              </button>
              <span className="bzv-dot-divider">•</span>
              <span className="bzv-stat-text"><MapPin size={13} /> Nairobi, Kenya</span>
              <span className="bzv-dot-divider">•</span>
              <button 
                type="button" 
                className="bzv-report-trigger" 
                onClick={() => setShowReportModal(true)}
              >
                <Flag size={12} /> Report
              </button>
            </div>

            {vendor.categories?.length > 0 && (
              <div className="bzv-tag-row">
                {vendor.categories.map((cat) => (
                  <span key={cat} className="bzv-category-pill">{cat}</span>
                ))}
              </div>
            )}
          </div>

          <div className="bzv-social-actions">
            {toWebsiteUrl(vendor.website) && (
              <a href={toWebsiteUrl(vendor.website)} target="_blank" rel="noopener noreferrer" className="bzv-link-pill">
                <Globe size={14} /> Website
              </a>
            )}
            {toInstagramUrl(vendor.instagram) && (
              <a href={toInstagramUrl(vendor.instagram)} target="_blank" rel="noopener noreferrer" className="bzv-link-pill">
                <Instagram size={14} /> Instagram
              </a>
            )}
            <VendorShareButton vendorName={vendor.brand_name} slug={vendor.slug} />
          </div>
        </div>

        {/* Navigation Tabs Bar */}
        <div className="bzv-navigation-tabs">
          <button
            type="button"
            className={`bzv-tab-item ${activeTab === "shop" ? "active" : ""}`}
            onClick={() => setActiveTab("shop")}
          >
            <ShoppingBag size={15} />
            <span>Shop</span>
            {products.length > 0 && <span className="bzv-tab-counter">{products.length}</span>}
          </button>

          {/* HIDDEN ON DESKTOP - ONLY VISIBLE ON MOBILE/TABLET */}
          <button
            type="button"
            className={`bzv-tab-item bzv-tab-mobile-only ${activeTab === "about" ? "active" : ""}`}
            onClick={() => setActiveTab("about")}
          >
            <Info size={15} />
            <span>About & Contact</span>
          </button>

          {gallery.length > 0 && (
            <button
              type="button"
              className={`bzv-tab-item ${activeTab === "gallery" ? "active" : ""}`}
              onClick={() => setActiveTab("gallery")}
            >
              <ImageIcon size={15} />
              <span>Gallery</span>
              <span className="bzv-tab-counter">{gallery.length}</span>
            </button>
          )}

          <button
            type="button"
            className={`bzv-tab-item ${activeTab === "reviews" ? "active" : ""}`}
            onClick={() => setActiveTab("reviews")}
          >
            <MessageSquare size={15} />
            <span>Reviews</span>
            <span className="bzv-tab-counter">{ratings.length}</span>
          </button>
        </div>
      </div>

      {/* ── Desktop Two-Column Main Layout ── */}
      <div className="bzv-desktop-split-layout">
        {/* Left Sticky Sidebar (Permanent Context on Desktop) */}
        <aside className="bzv-desktop-sidebar">
          {vendor.bio && (
            <div className="bzv-info-card">
              <h3 className="bzv-info-card-title">About {vendor.brand_name}</h3>
              <p className="bzv-info-card-text">{vendor.bio}</p>
            </div>
          )}

          {(vendor.contact_person || vendor.phone_number || vendor.email) && vendor.show_contact_public && (
            <div className="bzv-info-card">
              <h3 className="bzv-info-card-title">Contact Information</h3>
              <div className="bzv-contact-list">
                {vendor.contact_person && (
                  <div className="bzv-contact-item">
                    <User size={16} className="bzv-contact-icon" />
                    <div>
                      <span className="bzv-contact-sub">Representative</span>
                      <strong className="bzv-contact-val">{vendor.contact_person}</strong>
                    </div>
                  </div>
                )}

                {vendor.phone_number && (
                  <a className="bzv-contact-item bzv-contact-item--clickable" href={`tel:${vendor.phone_number}`}>
                    <Phone size={16} className="bzv-contact-icon" />
                    <div>
                      <span className="bzv-contact-sub">Phone / WhatsApp</span>
                      <strong className="bzv-contact-val">{vendor.phone_number}</strong>
                    </div>
                  </a>
                )}

                {vendor.email && (
                  <a className="bzv-contact-item bzv-contact-item--clickable" href={`mailto:${vendor.email}`}>
                    <Mail size={16} className="bzv-contact-icon" />
                    <div>
                      <span className="bzv-contact-sub">Email</span>
                      <strong className="bzv-contact-val">{vendor.email}</strong>
                    </div>
                  </a>
                )}
              </div>
            </div>
          )}
        </aside>

        {/* Right Dynamic Tab Main Pane */}
        <main className="bzv-main-pane">
          {/* SHOP TAB */}
          {activeTab === "shop" && (
            <div className="bzv-tab-body">
              {!canListProducts ? (
                <div className="bzv-preview-callout">
                  <div className="bzv-preview-tag">
                    <Sparkles size={14} /> Direct Checkout Rolling Out
                  </div>
                  <h3>{vendor.brand_name} Official Storefront</h3>
                  <p>Direct online ordering and instant checkout are coming soon to this page.</p>
                  <div className="bzv-preview-actions-row">
                    <button className="bzv-primary-action-btn" onClick={() => setActiveTab("about")}>
                      View Contact & Bio
                    </button>
                  </div>
                </div>
              ) : products.length === 0 ? (
                <div className="bzv-empty-card">
                  <ShoppingBag size={36} className="bzv-muted-icon" />
                  <h3>Storefront is being updated</h3>
                  <p>No products are listed right now. Check back soon!</p>
                </div>
              ) : (
                <>
                  <div className="bzv-toolbar-box">
                    <div className="bzv-search-bar">
                      <Search size={16} />
                      <input
                        type="text"
                        placeholder="Search items by name or keywords..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>

                    {productCategories.length > 1 && (
                      <div className="bzv-filter-pills-row">
                        {productCategories.map(cat => (
                          <button
                            key={cat}
                            className={`bzv-filter-pill ${selectedCategory === cat ? "active" : ""}`}
                            onClick={() => setSelectedCategory(cat)}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bzv-products-grid">
                    {filteredProducts.map((item) => {
                      const cardImg = item.images?.[0]?.url;
                      return (
                        <div 
                          key={item.id} 
                          className="bzv-product-card"
                        >
                          <div className="bzv-card-image-wrap" onClick={() => setSelectedProduct(item)}>
                            {cardImg ? (
                              <img src={cardImg} alt={item.title} loading="lazy" />
                            ) : (
                              <div className="bzv-card-image-fallback">No Image</div>
                            )}
                            <span className="bzv-card-price-tag">
                              KES {Number(item.price).toLocaleString()}
                            </span>
                          </div>

                          <div className="bzv-card-body">
                            {item.sku && <span className="bzv-card-sku">{item.sku}</span>}
                            <h3 className="bzv-card-title" onClick={() => setSelectedProduct(item)}>{item.title}</h3>
                            
                            <div className="bzv-card-footer">
                              <span className="bzv-stock-status">{item.inventory_qty > 0 ? `In Stock` : "Out of stock"}</span>
                              
                              <button 
                                type="button" 
                                className="bzv-card-add-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(item, 1);
                                }}
                              >
                                <Plus size={14} /> Add
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ABOUT TAB (MOBILE / TABLET ONLY) */}
          {activeTab === "about" && (
            <div className="bzv-tab-body bzv-about-stack">
              {vendor.bio && (
                <div className="bzv-info-card">
                  <h3 className="bzv-info-card-title">About {vendor.brand_name}</h3>
                  <p className="bzv-info-card-text">{vendor.bio}</p>
                </div>
              )}

              {(vendor.contact_person || vendor.phone_number || vendor.email) && vendor.show_contact_public && (
                <div className="bzv-info-card">
                  <h3 className="bzv-info-card-title">Direct Contact Information</h3>
                  <div className="bzv-contact-list">
                    {vendor.contact_person && (
                      <div className="bzv-contact-item">
                        <User size={16} className="bzv-contact-icon" />
                        <div>
                          <span className="bzv-contact-sub">Contact Representative</span>
                          <strong className="bzv-contact-val">{vendor.contact_person}</strong>
                        </div>
                      </div>
                    )}

                    {vendor.phone_number && (
                      <a className="bzv-contact-item bzv-contact-item--clickable" href={`tel:${vendor.phone_number}`}>
                        <Phone size={16} className="bzv-contact-icon" />
                        <div>
                          <span className="bzv-contact-sub">Phone / WhatsApp</span>
                          <strong className="bzv-contact-val">{vendor.phone_number}</strong>
                        </div>
                      </a>
                    )}

                    {vendor.email && (
                      <a className="bzv-contact-item bzv-contact-item--clickable" href={`mailto:${vendor.email}`}>
                        <Mail size={16} className="bzv-contact-icon" />
                        <div>
                          <span className="bzv-contact-sub">Email Address</span>
                          <strong className="bzv-contact-val">{vendor.email}</strong>
                        </div>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* REVIEWS TAB */}
          {activeTab === "reviews" && (
            <div className="bzv-tab-body">
              <div className="bzv-info-card">
                <div className="bzv-card-header-between">
                  <div>
                    <h3 className="bzv-info-card-title">Customer Reviews</h3>
                    <div className="bzv-score-summary">
                      <Star size={18} className="bzv-star-filled" />
                      <span className="bzv-score-number">{avgRating}</span>
                      <span className="bzv-score-sub">based on {ratings.length} ratings</span>
                    </div>
                  </div>
                  <button className="bzv-primary-action-btn" onClick={() => setShowReviewModal(true)}>
                    <Star size={14} /> Write Review
                  </button>
                </div>

                {ratings.length === 0 ? (
                  <div className="bzv-empty-reviews-box">
                    <MessageSquare size={32} className="bzv-muted-icon" />
                    <p>No customer reviews submitted yet.</p>
                    <button className="bzv-link-pill" onClick={() => setShowReviewModal(true)}>
                      Be the first to leave a review
                    </button>
                  </div>
                ) : (
                  <div className="bzv-reviews-list">
                    {ratings.map((r) => (
                      <div key={r.id} className="bzv-review-item">
                        <div className="bzv-review-header">
                          <div>
                            <strong className="bzv-reviewer-name">{r.reviewer_name}</strong>
                            <span className="bzv-verified-buyer-pill">Verified Customer</span>
                          </div>
                          <div className="bzv-stars-row">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={13} className={i < r.rating ? "bzv-star-filled" : "bzv-star-empty"} />
                            ))}
                          </div>
                        </div>
                        {r.comment && <p className="bzv-review-text">{r.comment}</p>}
                        <span className="bzv-review-timestamp">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GALLERY TAB */}
          {activeTab === "gallery" && (
            <div className="bzv-tab-body">
              <div className="bzv-photo-grid">
                {gallery.map((entry, idx) => (
                  <button
                    key={entry.path || idx}
                    type="button"
                    className="bzv-photo-tile"
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img src={entry.url} alt="Gallery item" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── FLOATING VENDOR CART BADGE ── */}
      {cartItemCount > 0 && (
        <button 
          className="bzv-floating-cart-badge"
          onClick={() => setShowCartDrawer(true)}
        >
          <div className="bzv-cart-badge-icon">
            <ShoppingCart size={18} />
            <span className="bzv-cart-count">{cartItemCount}</span>
          </div>
          <span className="bzv-cart-badge-text">View Shopping Bag</span>
          <span className="bzv-cart-badge-total">KES {cartTotal.toLocaleString()}</span>
        </button>
      )}

      {/* ── VENDOR SHOPPING BAG DRAWER ── */}
      {showCartDrawer && (
        <div className="bzv-sheet-overlay" onClick={() => setShowCartDrawer(false)}>
          <div className="bzv-sheet-panel" onClick={(e) => e.stopPropagation()}>
            <div className="bzv-sheet-header">
              <div>
                <h2>{vendor.brand_name} Bag</h2>
                <span className="bzv-score-sub">{cartItemCount} {cartItemCount === 1 ? 'item' : 'items'} selected</span>
              </div>
              <button className="bzv-sheet-close-btn" onClick={() => setShowCartDrawer(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="bzv-sheet-body">
              {cart.length === 0 ? (
                <div className="bzv-empty-reviews-box">
                  <ShoppingBag size={36} className="bzv-muted-icon" />
                  <p>Your shopping bag is empty.</p>
                </div>
              ) : (
                <div className="bzv-cart-items-list">
                  {cart.map((item) => (
                    <div key={item.id} className="bzv-cart-item">
                      <div className="bzv-cart-item-img">
                        <img src={item.images?.[0]?.url || "/placeholder.png"} alt={item.title} />
                      </div>

                      <div className="bzv-cart-item-details">
                        <h4>{item.title}</h4>
                        <span className="bzv-cart-item-price">
                          KES {Number(item.price).toLocaleString()}
                        </span>

                        <div className="bzv-cart-qty-controls">
                          <button onClick={() => updateCartQty(item.id, -1)}>
                            <Minus size={12} />
                          </button>
                          <span>{item.quantity}</span>
                          <button onClick={() => updateCartQty(item.id, 1)}>
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>

                      <button 
                        className="bzv-cart-item-remove"
                        onClick={() => removeFromCart(item.id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="bzv-sheet-footer">
                <div className="bzv-cart-summary-row">
                  <span>Subtotal</span>
                  <strong>KES {cartTotal.toLocaleString()}</strong>
                </div>

                {vendor.phone_number ? (
                  <a
                    href={formatMultiItemWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bzv-whatsapp-cta-btn"
                  >
                    <MessageSquare size={18} /> Checkout via WhatsApp / M-Pesa
                  </a>
                ) : vendor.email ? (
                  <a
                    href={`mailto:${vendor.email}?subject=Order Inquiry (${cartItemCount} items)&body=${encodeURIComponent(`Hi ${vendor.brand_name},\n\nI want to purchase:\n` + cart.map(i => `- ${i.quantity}x${i.title}`).join('\n') + `\n\nTotal: KES ${cartTotal}`)}`}
                    className="bzv-primary-action-btn"
                  >
                    <Mail size={18} /> Email Vendor to Checkout
                  </a>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PRODUCT QUICK VIEW DRAWER ── */}
      {selectedProduct && (
        <div className="bzv-sheet-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="bzv-sheet-panel" onClick={(e) => e.stopPropagation()}>
            <button className="bzv-sheet-close-btn bzv-sheet-close-floating" onClick={() => setSelectedProduct(null)}>
              <X size={20} />
            </button>

            <div className="bzv-sheet-body">
              <div className="bzv-quick-view-media">
                <div className="bzv-quick-view-main">
                  <img 
                    src={selectedProduct.images?.[productActiveImg]?.url || "/placeholder.png"} 
                    alt={selectedProduct.title} 
                  />
                </div>
                {selectedProduct.images?.length > 1 && (
                  <div className="bzv-quick-view-thumbs">
                    {selectedProduct.images.map((img, idx) => (
                      <button
                        key={idx}
                        className={`bzv-thumb-btn ${productActiveImg === idx ? "active" : ""}`}
                        onClick={() => setProductActiveImg(idx)}
                      >
                        <img src={img.url} alt={`Thumb ${idx}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bzv-quick-view-meta">
                <div className="bzv-quick-view-head">
                  {selectedProduct.sku && <span className="bzv-card-sku">{selectedProduct.sku}</span>}
                  <h2 className="bzv-quick-view-title">{selectedProduct.title}</h2>
                  <div className="bzv-quick-view-price">KES {Number(selectedProduct.price).toLocaleString()}</div>
                </div>

                {selectedProduct.description && (
                  <div className="bzv-quick-view-desc">
                    <h4>Description</h4>
                    <p>{selectedProduct.description}</p>
                  </div>
                )}

                <div className="bzv-quick-view-checklist">
                  <div className="bzv-checklist-row">
                    <ShieldCheck size={16} color="#10b981" />
                    <span>Verified Vendor • {vendor.brand_name}</span>
                  </div>
                  <div className="bzv-checklist-row">
                    <Tag size={16} />
                    <span>In Stock: {selectedProduct.inventory_qty} units</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bzv-sheet-footer">
              <button
                className="bzv-primary-action-btn"
                onClick={() => {
                  addToCart(selectedProduct, 1);
                  setSelectedProduct(null);
                  setShowCartDrawer(true);
                }}
              >
                <Plus size={18} /> Add to Shopping Bag
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REVIEWS & REPORT MODALS */}
      {showReviewModal && (
        <div className="bzv-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="bzv-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="bzv-modal-top">
              <h3>Write a Review for {vendor.brand_name}</h3>
              <button className="bzv-sheet-close-btn" onClick={() => setShowReviewModal(false)}><X size={18} /></button>
            </div>

            {reviewSuccess ? (
              <div className="bzv-success-box">
                <CheckCircle2 size={24} color="#10b981" />
                <span>Thank you! Your review has been submitted.</span>
              </div>
            ) : (
              <form onSubmit={handleReviewSubmit} className="bzv-modal-form">
                <div className="bzv-form-group">
                  <label>Overall Rating</label>
                  <div className="bzv-star-selector">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        type="button"
                        key={num}
                        className={num <= reviewStars ? "active" : ""}
                        onClick={() => setReviewStars(num)}
                      >
                        <Star size={22} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bzv-form-group">
                  <label>Your Name *</label>
                  <input type="text" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} required />
                </div>

                <div className="bzv-form-group">
                  <label>Your Email *</label>
                  <input type="email" value={reviewerEmail} onChange={(e) => setReviewerEmail(e.target.value)} required />
                </div>

                <div className="bzv-form-group">
                  <label>Review Comment</label>
                  <textarea rows={3} value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Share details of your experience..." />
                </div>

                <button type="submit" className="bzv-primary-action-btn" disabled={reviewSubmitting}>
                  {reviewSubmitting ? "Submitting…" : "Post Review"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="bzv-modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="bzv-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="bzv-modal-top">
              <h3>Report {vendor.brand_name}</h3>
              <button className="bzv-sheet-close-btn" onClick={() => setShowReportModal(false)}><X size={18} /></button>
            </div>

            {reportSuccess ? (
              <div className="bzv-success-box">
                <CheckCircle2 size={24} color="#10b981" />
                <span>Report submitted. Our team will review this vendor shortly.</span>
              </div>
            ) : (
              <form onSubmit={handleReportSubmit} className="bzv-modal-form">
                <div className="bzv-form-group">
                  <label>Reason for Report</label>
                  <select value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                    <option value="fraud">Suspected Fraud or Scam</option>
                    <option value="fake_vendor">Impersonation / Fake Vendor</option>
                    <option value="inappropriate_content">Inappropriate Content</option>
                    <option value="other">Other Issue</option>
                  </select>
                </div>

                <div className="bzv-form-group">
                  <label>Your Email Address *</label>
                  <input type="email" value={reporterEmail} onChange={(e) => setReporterEmail(e.target.value)} required placeholder="So we can follow up with you" />
                </div>

                <div className="bzv-form-group">
                  <label>Additional Details</label>
                  <textarea rows={3} value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder="Provide any details to help our team investigate..." />
                </div>

                <button type="submit" className="bzv-danger-action-btn" disabled={reportSubmitting}>
                  {reportSubmitting ? "Submitting…" : "Submit Report"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* FULLSCREEN LIGHTBOX */}
      {isLightboxOpen && (
        <div className="bzv-lightbox-overlay" onClick={closeLightbox}>
          <button className="bzv-lightbox-close-btn" onClick={closeLightbox}>
            <X size={22} />
          </button>

          {gallery.length > 1 && (
            <button
              type="button"
              className="bzv-lightbox-arrow bzv-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
            >
              <ChevronLeft size={28} />
            </button>
          )}

          <figure className="bzv-lightbox-stage" onClick={(e) => e.stopPropagation()}>
            <img
              key={gallery[lightboxIndex]?.path || lightboxIndex}
              src={gallery[lightboxIndex]?.url}
              alt={gallery[lightboxIndex]?.name || "Gallery Full View"}
              className="bzv-lightbox-img"
            />
            {gallery[lightboxIndex]?.name && (
              <figcaption className="bzv-lightbox-caption-text">
                {gallery[lightboxIndex].name}
              </figcaption>
            )}
          </figure>

          {gallery.length > 1 && (
            <>
              <button
                type="button"
                className="bzv-lightbox-arrow bzv-lightbox-next"
                onClick={(e) => { e.stopPropagation(); showNext(); }}
              >
                <ChevronRight size={28} />
              </button>
              <div className="bzv-lightbox-counter-badge">
                {lightboxIndex + 1} / {gallery.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorPublicProfile;